import NiceModal, { useModal } from '@ebay/nice-modal-react';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';

import AutoComplete from 'components/field/AutoComplete';
import Button from 'components/field/Button';
import EnumSelect from 'components/field/EnumSelect';
import IntegerInput from 'components/field/IntegerInput';
import Form from 'components/form/Form';
import FormItem from 'components/form/FormItem';
import Modal from 'components/global/Modal';
import Typography from 'components/global/Typography';
import { ModalActions, ModalContent, ModalTitle } from 'components/global/styled/Modal.styled';
import { updateDynamicListData } from 'components/list/dynamicListSlice';

import useAlert from 'hooks/useAlert';
import useAxiosPrivate from 'hooks/useAxiosPrivate';
import useModalRouter from 'hooks/useModalRouter';
import { usePrompt } from 'hooks/usePrompt';

import { PRICE_CONFIGURATIONS, SHIPPING_PARTNERS } from 'services/consts';

import { getCityByCountry, getCountryList, getDistrictByCity } from 'stores/globalAddressSlice';

import AppLogger from 'utils/AppLogger';
import { DYNAMICLISTS } from 'utils/constants';

const validateOptions = { shouldValidate: true, shouldDirty: true };

const BulletDistrictSLAForm = NiceModal.create(({ id, modalName, mode, settingsID, contractID }) => {
	const modal = useModal();
	const dispatch = useDispatch();
	const axiosPrivate = useAxiosPrivate();
	const { showError, showSuccess } = useAlert();
	const [data, setData] = useState({
		origin: {
			country: [],
			city: [],
			district: []
		},
		destination: {
			country: [],
			city: [],
			district: []
		}
	});
	const { closeModal } = useModalRouter();
	const defaultColSpan = {
		md: 6
	};

	const schema = useMemo(
		() =>
			yup
				.object()
				.shape({
					pickupSla: yup.string().nullable().required(),
					deliverSla: yup.string().nullable().required(),
					shippingContractID: yup.number().nullable().required(),
					totalDeliveryCutOff: yup.string().nullable().required(),
					originCity: yup.string().nullable().required(),
					originDistrict: yup.string().nullable().required(),
					originCountry: yup.string().nullable().required(),
					destinationCountry: yup.string().nullable().required(),
					destinationCity: yup.string().nullable().required(),
					destinationDistrict: yup.string().nullable().required()
				})
				.required(),
		[]
	);

	const fetchCityByCountryData = useCallback(async (country) => {
		try {
			const response = await dispatch(getCityByCountry(country)).unwrap();
			return response?.data?.map((item) => item?.name) ?? [];
		} catch (error) {
			AppLogger.error(error);
			showError();
			return [];
		}
	}, []);

	const fetchDistrictsByCity = useCallback(async (cityName) => {
		try {
			const response = await dispatch(getDistrictByCity(cityName)).unwrap();
			return response?.data?.map((item) => item?.name) ?? [];
		} catch (error) {
			AppLogger.error(error);
			showError({
				message: error?.response?.data.code || error?.response?.data?.message
			});
			return [];
		}
	}, []);

	const setEditDependentData = useCallback(
		async (editData) => {
			const [originCities, originDistricts, destinationCities, destinationDistricts] = await Promise.all([
				editData.originCountry ? fetchCityByCountryData(editData.originCountry) : [],
				editData.originCity ? fetchDistrictsByCity(editData.originCity) : [],
				editData.destinationCountry ? fetchCityByCountryData(editData.destinationCountry) : [],
				editData.destinationCity ? fetchDistrictsByCity(editData.destinationCity) : []
			]);
			setData((prev) => ({
				...prev,
				origin: {
					...prev.origin,
					city: originCities,
					district: originDistricts
				},
				destination: {
					...prev.destination,
					city: destinationCities,
					district: destinationDistricts
				}
			}));
		},
		[fetchCityByCountryData, fetchDistrictsByCity]
	);

	const fillForm = useCallback(async () => {
		const defaultValues = {
			totalDeliveryCutOff: null,
			pickupSla: null,
			deliverSla: null,
			shippingContractID: null,
			originCity: null,
			originDistrict: null,
			originCountry: null,
			destinationCity: null,
			destinationDistrict: null,
			destinationCountry: null
		};

		if (!id) {
			return defaultValues;
		}

		try {
			const response = await axiosPrivate.post(PRICE_CONFIGURATIONS.GET_BULLET_DISTRICT_SLA, { id });
			const { data, status } = response;

			if (status === 200) {
				setEditDependentData(data);
				return {
					...data,
					pickupSla: Number(data.pickupSla),
					deliverSla: Number(data.deliverSla),
					totalDeliveryCutOff: Number(data.totalDeliveryCutOff),
					shippingContractID: data.shippingContract?.id ?? null
				};
			} else {
				AppLogger.error(response);
			}
		} catch (error) {
			AppLogger.error(error);
			showError({
				message: error.response?.data?.code || error.response?.data?.message
			});
		}
	}, [id]);

	const formMethods = useForm({
		resolver: yupResolver(schema),
		defaultValues: fillForm
	});

	const {
		handleSubmit,
		setValue,
		watch,
		formState: { isDirty, isSubmitSuccessful, isSubmitting, isLoading: isFormLoading }
	} = formMethods;

	const isLoading = isFormLoading || isSubmitting;

	usePrompt(isDirty && !isSubmitSuccessful && !isSubmitting);

	const handleCloseModal = useCallback(() => {
		if (modalName) {
			closeModal();
		} else {
			modal.remove();
		}
	}, [modalName]);

	const onSubmit = (data) => {
		const url = settingsID
			? SHIPPING_PARTNERS.ADD_BULLET_DISTRICT_SLA_METHOD
			: PRICE_CONFIGURATIONS.ADD_BULLET_DISTRICT_SLA;
		axiosPrivate
			.post(url, {
				id: id ?? null,
				deliveryCompanySettingsId: settingsID ?? null,
				...data,
				shippingContract: {
					id: data.shippingContractID
				}
			})
			.then((response) => {
				const { status } = response;

				if (status === 200) {
					dispatch(updateDynamicListData(DYNAMICLISTS.PRICE_CONFIGURATIONS_BULLET_DISTRICT_SLA));
					showSuccess();
					handleCloseModal();
				}
			})
			.catch((error) => {
				showError({
					message: error.response?.data.code || error.response?.data.message
				});
			});
	};

	const fetchAutocompleteData = useCallback(async (props) => {
		const data = await axiosPrivate
			.post(PRICE_CONFIGURATIONS.GET_SHIPPING_CONTRACT_AUTOCOMPLETE_LIST)
			.catch((error) => {
				showError();
				AppLogger.error(error);
			});

		return data.data.filter((item) =>
			props?.keyword
				? item?.name?.toLowerCase().includes(props?.keyword.toLowerCase()) ||
					item?.deliveryCompanySettingsId?.toString().toLowerCase().includes(props?.keyword.toLowerCase())
				: props?.IDs
					? props?.IDs.includes(item.ID)
					: true
		);
	}, []);

	const shippingContractAutocompleteColumns = useMemo(
		() => [
			{ accessor: 'deliveryCompanySettingsId', width: 50 },
			{ accessor: 'companyName', width: 100 },
			{ accessor: 'name', width: 200 }
		],
		[]
	);

	const fetchCountryList = useCallback(async () => {
		try {
			const response = await dispatch(getCountryList()).unwrap();

			setData((prev) => ({
				...prev,
				origin: {
					...prev.origin,
					country: response?.map((item) => item?.shortCode) ?? []
				},
				destination: {
					...prev.destination,
					country: response?.map((item) => item?.shortCode) ?? []
				}
			}));
		} catch (error) {
			AppLogger.error(error);
			showError({ message: error?.response?.data.code || error?.response?.data.message });
			return [];
		}
	}, [dispatch]);

	useEffect(() => {
		fetchCountryList();
	}, []);

	return (
		<Modal fullwidth name={modalName} maxWidth="medium" className="modal-bulletDistrictSLAForm">
			<ModalTitle>
				<Typography variant="p1">bulletDistrictSLAForm</Typography>
			</ModalTitle>
			<ModalContent>
				<Form formMethods={formMethods}>
					<FormItem
						label={settingsID || mode === 'editConnectedPartner' ? 'deliveryCompany' : 'shippingContract'}
						isLoading={isLoading}
						required>
						<AutoComplete
							disabled={settingsID || mode === 'editConnectedPartner'}
							fetchFunction={fetchAutocompleteData}
							columns={shippingContractAutocompleteColumns}
							value={contractID ? contractID : watch('shippingContractID')}
							setValue={(value) => {
								setValue('shippingContractID', value?.ID ?? null, validateOptions);
							}}
							name={'shippingContractID'}
							selectionLabelAccessor={['deliveryCompanySettingsId', 'name']}
						/>
					</FormItem>

					<FormItem colSpan={defaultColSpan} label="pickupSla" isLoading={isLoading} required>
						<IntegerInput name="pickupSla" decimalLimit={1} unit="minutes" />
					</FormItem>

					<FormItem colSpan={defaultColSpan} label="deliverSla" isLoading={isLoading} required>
						<IntegerInput name="deliverSla" decimalLimit={1} unit="minutes" />
					</FormItem>

					<FormItem colSpan={defaultColSpan} label="totalDeliveryCutOff" isLoading={isLoading} required>
						<IntegerInput name="totalDeliveryCutOff" decimalLimit={1} unit="minutes" />
					</FormItem>

					<FormItem colSpan={defaultColSpan} label="originCountry" isLoading={isLoading} required>
						<EnumSelect
							name="originCountry"
							value={watch('originCountry')}
							data={data.origin.country ?? []}
							labelPrefix="country."
							setValue={(value) => {
								setValue('originCountry', value ?? null, validateOptions);
								setValue('originCity', null, validateOptions);
								setValue('originDistrict', null, validateOptions);
								if (value) {
									fetchCityByCountryData(value).then((cities) => {
										setData((prev) => ({
											...prev,
											origin: {
												...prev.origin,
												city: cities,
												district: []
											}
										}));
									});
								}
							}}
							selectionLabelAccessor={['name']}
						/>
					</FormItem>
					<FormItem colSpan={defaultColSpan} label="originCity" isLoading={isLoading} required>
						<EnumSelect
							name="originCity"
							data={data.origin.city ?? []}
							value={watch('originCity') ?? null}
							setValue={(value) => {
								setValue('originCity', value, validateOptions);
								setValue('originDistrict', null, validateOptions);
								if (value) {
									fetchDistrictsByCity(value).then((districts) => {
										setData((prev) => ({
											...prev,
											origin: {
												...prev.origin,
												district: districts
											}
										}));
									});
								}
							}}
							disabled={!watch('originCountry')}
						/>
					</FormItem>
					<FormItem colSpan={defaultColSpan} label="originDistrict" isLoading={isLoading} required>
						<EnumSelect
							name="originDistrict"
							data={data.origin.district ?? []}
							setValue={(value) => {
								setValue('originDistrict', value, validateOptions);
							}}
							value={watch('originDistrict') ?? null}
							disabled={!watch('originCity')}
						/>
					</FormItem>

					<FormItem colSpan={defaultColSpan} label="destinationCountry" isLoading={isLoading} required>
						<EnumSelect
							name="destinationCountry"
							value={watch('destinationCountry')}
							data={data.origin.country ?? []}
							labelPrefix="country."
							setValue={(value) => {
								setValue('destinationCountry', value ?? null, validateOptions);
								setValue('destinationCity', null, validateOptions);
								setValue('destinationDistrict', null, validateOptions);
								if (value) {
									fetchCityByCountryData(value).then((cities) => {
										setData((prev) => ({
											...prev,
											destination: {
												...prev.destination,
												city: cities,
												district: []
											}
										}));
									});
								}
							}}
							selectionLabelAccessor={['name']}
						/>
					</FormItem>
					<FormItem colSpan={defaultColSpan} label="destinationCity" isLoading={isLoading} required>
						<EnumSelect
							name="destinationCity"
							data={data.destination.city ?? []}
							value={watch('destinationCity') ?? null}
							setValue={(value) => {
								setValue('destinationCity', value, validateOptions);
								setValue('destinationDistrict', null, validateOptions);
								if (value) {
									fetchDistrictsByCity(value).then((districts) => {
										setData((prev) => ({
											...prev,
											destination: {
												...prev.destination,
												district: districts
											}
										}));
									});
								}
							}}
							disabled={!watch('destinationCountry')}
						/>
					</FormItem>
					<FormItem colSpan={defaultColSpan} label="destinationDistrict" isLoading={isLoading} required>
						<EnumSelect
							name="destinationDistrict"
							data={data.destination.district ?? []}
							setValue={(value) => {
								setValue('destinationDistrict', value, validateOptions);
							}}
							value={watch('destinationDistrict') ?? null}
							disabled={!watch('destinationCity')}
						/>
					</FormItem>
				</Form>
			</ModalContent>

			<ModalActions>
				<Button label={'cancel'} variant="outline" onClick={handleCloseModal} />
				<Button
					loading={isLoading}
					label={id ? 'save' : 'button.add'}
					disabled={!isDirty}
					disabledTooltip={!isDirty && 'formInfo.noChanges'}
					onClick={handleSubmit(onSubmit, (errors) => {
						AppLogger.log(errors);
					})}
				/>
			</ModalActions>
		</Modal>
	);
});

export default BulletDistrictSLAForm;
